import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import {
  Users, Home, LogOut, Menu, ChevronRight, Key, GraduationCap,
  Layers, Wrench, Building2, Award, BadgeCheck, Boxes, Info, Users2, CalendarCheck, Clock, CalendarClock, BarChart3, ClipboardCheck, ClipboardList, FileQuestion, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const hrNavItems = [
  { path: '/hr', icon: Users, label: 'Employees', end: true },
  { path: '/hr/reporting-tree', icon: Users2, label: 'Reporting Tree' },
  { path: '/hr/skill-groups', icon: Layers, label: 'Skill Groups' },
  { path: '/hr/skills', icon: Wrench, label: 'Skills' },
  { path: '/hr/functions', icon: Building2, label: 'Functions' },
  { path: '/hr/positions', icon: BadgeCheck, label: 'Positions' },
  { path: '/hr/qualifications', icon: Award, label: 'Qualifications' },
  { path: '/hr/functional-positions', icon: Boxes, label: 'Functional Positions' },
  { path: '/hr/trainings', icon: GraduationCap, label: 'Trainings' },
  { path: '/hr/training-sessions', icon: CalendarCheck, label: 'Training Sessions' },
  { path: '/hr/competency-tests', icon: FileQuestion, label: 'Competency Tests' },
  { path: '/hr/recruitment', icon: Briefcase, label: 'Recruitment' },
  { path: '/hr/training-plan', icon: CalendarClock, label: 'Training Plan' },
  { path: '/hr/pending-skill-updates', icon: Clock, label: 'Pending Skill Updates' },
  { path: '/hr/pending-evaluations', icon: ClipboardCheck, label: 'Pending Evaluations' },
  { path: '/hr/completed-evaluations', icon: ClipboardList, label: 'Completed Evaluations' },
  { path: '/hr/reports', icon: BarChart3, label: 'Reports' },
  { path: '/hr/skill-levels', icon: Info, label: 'Skill Levels' },
];

export default function HRLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // A Recruitment-only manager (no HR & Training access, not admin) can only use
  // the Recruitment area: hide the rest of the HR nav and keep them on it.
  const isAdmin = user?.role === 'admin';
  const hasHrTraining = isAdmin || (user?.app_roles?.hr_training && user.app_roles.hr_training !== 'none');
  const recruitmentOnly = !hasHrTraining && (user?.app_roles?.recruitment && user.app_roles.recruitment !== 'none');
  const navItems = recruitmentOnly ? hrNavItems.filter(i => i.path === '/hr/recruitment') : hrNavItems;

  useEffect(() => {
    if (recruitmentOnly && location.pathname.startsWith('/hr') && location.pathname !== '/hr/recruitment') {
      navigate('/hr/recruitment', { replace: true });
    }
  }, [recruitmentOnly, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-background flex">
      <Toaster position="top-right" richColors closeButton />
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed lg:sticky top-0 left-0 z-30 h-screen w-60 bg-slate-50 border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-4 border-b border-border">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-erp">
            <Home className="w-3.5 h-3.5" /><span>ERP Home</span>
          </button>
          <h2 className="font-heading font-semibold text-base mt-2 tracking-tight flex items-center gap-2">
            {recruitmentOnly ? <Briefcase className="w-4 h-4 text-fuchsia-600" /> : <GraduationCap className="w-4 h-4 text-pink-600" />}
            {recruitmentOnly ? 'Recruitment' : 'HR & Training'}
          </h2>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.end}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? 'bg-pink-50 text-pink-700 font-medium border border-pink-200' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              onClick={() => setSidebarOpen(false)} data-testid={`nav-${item.label.toLowerCase().replace(/[\s\/&]/g, '-')}`}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md">
            <div className="w-7 h-7 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center text-xs font-semibold">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden p-3 border-b border-border flex items-center gap-3 bg-background sticky top-0 z-10">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5" />
          </Button>
          <span className="font-heading font-semibold text-sm">HR &amp; Training</span>
        </div>
        <div className="p-4 md:p-6"><PageBreadcrumb /><Outlet /></div>
      </main>
    </div>
  );
}
